import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";
import { AuthDialog } from "@/components/AuthDialog";
import { supabase } from "@/integrations/supabase/client";

type AuthTab = "signin" | "signup";

export interface AuthPromptOptions {
  /** Short action label, e.g. "like posts", "join this group" */
  action?: string;
  /** Override description shown in the dialog */
  description?: string;
  /** Which tab to open. Defaults to "signup" — most prompts encourage joining */
  defaultTab?: AuthTab;
}

interface AuthPromptContextValue {
  /** Open the auth modal with a contextual message. Always returns false (caller should return early). */
  promptAuth: (options?: AuthPromptOptions) => false;
  /**
   * Run `action` only if signed in; otherwise open the auth modal with a message.
   * Returns true if the action ran, false if the user was prompted.
   */
  requireAuth: (
    action: () => void | Promise<void>,
    options?: AuthPromptOptions
  ) => Promise<boolean>;
  isOpen: boolean;
}

const AuthPromptContext = createContext<AuthPromptContextValue | null>(null);

export const AuthPromptProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<AuthTab>("signup");
  const [description, setDescription] = useState<string | undefined>(undefined);

  const buildDescription = (opts?: AuthPromptOptions) => {
    if (opts?.description) return opts.description;
    if (opts?.action) {
      return `Create a free account to ${opts.action}. Already a member? Sign in instead.`;
    }
    return "Create a free account to join the conversation, save posts, and unlock the full community.";
  };

  const promptAuth = useCallback((opts?: AuthPromptOptions) => {
    setDescription(buildDescription(opts));
    setTab(opts?.defaultTab ?? "signup");
    setIsOpen(true);
    return false as const;
  }, []);

  const requireAuth = useCallback(
    async (action: () => void | Promise<void>, opts?: AuthPromptOptions) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        promptAuth(opts);
        return false;
      }
      await action();
      return true;
    },
    [promptAuth]
  );

  const value = useMemo(
    () => ({ promptAuth, requireAuth, isOpen }),
    [promptAuth, requireAuth, isOpen]
  );

  return (
    <AuthPromptContext.Provider value={value}>
      {children}
      <AuthDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        defaultTab={tab}
        message={description}
      />
    </AuthPromptContext.Provider>
  );
};

export const useAuthPrompt = () => {
  const ctx = useContext(AuthPromptContext);
  if (!ctx) {
    // Safe fallback so hooks/components used outside provider don't crash
    return {
      promptAuth: (() => false) as AuthPromptContextValue["promptAuth"],
      requireAuth: (async (action: () => void | Promise<void>) => {
        await action();
        return true;
      }) as AuthPromptContextValue["requireAuth"],
      isOpen: false,
    };
  }
  return ctx;
};
